import React, { useState, useEffect } from 'react';
import db from '../database/db';
import './ProgressionScreen.css';

function ProgressionScreen({ userId, onProgressionUpdated }) {
  const [activeProgression, setActiveProgression] = useState(null);
  const [previousProgressions, setPreviousProgressions] = useState([]);
  const [showNewProgressionForm, setShowNewProgressionForm] = useState(false);
  const [rm, setRm] = useState('');
  const [increment, setIncrement] = useState(2.5);
  const [todayWeight, setTodayWeight] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProgressions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadProgressions = async () => {
    try {
      setLoading(true);
      const active = await db.getActiveProgression(userId);
      const all = await db.getAllProgressions(userId);
      
      setActiveProgression(active);
      setPreviousProgressions(all.filter(p => p.status === 'completed'));
      
      if (active) {
        const trainings = await db.getTrainingsByProgression(active.id);
        if (trainings.length > 0) {
          const lastTraining = trainings[0];
          setTodayWeight(parseFloat(lastTraining.weight) + parseFloat(active.increment));
        } else {
          setTodayWeight(parseFloat(active.start_weight));
        }
      }
    } catch (error) {
      console.error('Error loading progressions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProgression = async (e) => {
    e.preventDefault();
    
    if (!rm || parseFloat(rm) <= 0) {
      alert('Por favor, introduce un RM válido');
      return;
    }

    try {
      const startWeight = Math.round((parseFloat(rm) * 0.5) * 2) / 2;
      await db.createProgression(
        userId,
        parseFloat(rm),
        parseFloat(increment),
        startWeight
      );
      
      await loadProgressions();
      setShowNewProgressionForm(false);
      setRm('');
      setIncrement(2.5);
    } catch (error) {
      console.error('Error creating progression:', error);
      alert('Error al crear la progresión');
    }
  };

  const handleCompleteProgression = async () => {
    if (!activeProgression) return;
    
    if (window.confirm('¿Estás seguro de que quieres finalizar esta progresión?')) {
      try {
        await db.completeProgression(activeProgression.id);
        await loadProgressions();
        if (onProgressionUpdated) onProgressionUpdated();
      } catch (error) {
        console.error('Error completing progression:', error);
        alert('Error al finalizar la progresión');
      }
    }
  };

  if (loading) {
    return (
      <div className="progression-screen">
        <div className="loading">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="progression-screen">
      <div className="progression-container">
        <h1 className="progression-title">Progresión</h1>

        {!activeProgression && !showNewProgressionForm && (
          <div className="no-progression">
            <p>No hay una progresión activa.</p>
            <button
              onClick={() => setShowNewProgressionForm(true)}
              className="btn-primary"
            >
              Crear Nueva Progresión
            </button>
          </div>
        )}

        {showNewProgressionForm && (
          <div className="new-progression-form">
            <h2>Nueva Progresión</h2>
            <form onSubmit={handleCreateProgression}>
              <div className="form-group">
                <label htmlFor="rm">RM de Press Banca (kg)</label>
                <input
                  id="rm"
                  type="number"
                  step="0.5"
                  min="20"
                  max="500"
                  value={rm}
                  onChange={(e) => setRm(e.target.value)}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="increment">Incremento de peso (kg)</label>
                <select
                  id="increment"
                  value={increment}
                  onChange={(e) => setIncrement(parseFloat(e.target.value))}
                  className="form-select"
                >
                  <option value={1}>1 kg</option>
                  <option value={1.5}>1.5 kg</option>
                  <option value={2}>2 kg</option>
                  <option value={2.5}>2.5 kg</option>
                  <option value={3}>3 kg</option>
                  <option value={3.5}>3.5 kg</option>
                  <option value={4}>4 kg</option>
                  <option value={4.5}>4.5 kg</option>
                  <option value={5}>5 kg</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  Crear Progresión
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowNewProgressionForm(false);
                    setRm('');
                  }}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {activeProgression && (
          <div className="active-progression">
            <div className="progression-header">
              <h2>Progresión Actual</h2>
              <button
                onClick={handleCompleteProgression}
                className="btn-danger"
              >
                Finalizar Progresión
              </button>
            </div>
            
            <div className="progression-info">
              <div className="info-item">
                <span className="info-label">RM:</span>
                <span className="info-value">{activeProgression.rm} kg</span>
              </div>
              <div className="info-item">
                <span className="info-label">Incremento:</span>
                <span className="info-value">{activeProgression.increment} kg</span>
              </div>
              <div className="info-item">
                <span className="info-label">Peso inicial:</span>
                <span className="info-value">{activeProgression.start_weight} kg</span>
              </div>
              <div className="info-item highlight">
                <span className="info-label">Peso del día:</span>
                <span className="info-value">{todayWeight ? `${todayWeight} kg` : 'Calculando...'}</span>
              </div>
            </div>
          </div>
        )}

        {previousProgressions.length > 0 && (
          <div className="previous-progressions">
            <h2>Progresiones Anteriores</h2>
            <div className="progressions-list">
              {previousProgressions.map((progression) => (
                <div key={progression.id} className="progression-card">
                  <div className="progression-card-header">
                    <span className="progression-date">
                      {new Date(progression.started_at).toLocaleDateString('es-ES')}
                    </span>
                    <span className="progression-status completed">Completada</span>
                  </div>
                  <div className="progression-card-body">
                    <div>RM: {progression.rm} kg</div>
                    <div>Incremento: {progression.increment} kg</div>
                    <div>Peso inicial: {progression.start_weight} kg</div>
                    {progression.completed_at && (
                      <div className="completed-date">
                        Finalizada: {new Date(progression.completed_at).toLocaleDateString('es-ES')}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProgressionScreen;
